/**
 * URL Sync Component
 * 
 * Integrates URL synchronization with XState app state.
 * This component should be rendered at the top level of the app.
 */

import React, { useEffect, useContext, useRef, useState } from 'react';
import { useActor } from '@xstate/react';
import { GlobalStateContext } from '../state/app/index';
import { createUrlSyncService } from '../services/urlSyncService';
import LoadingToast from './LoadingToast';
import interactionLedger from '../utils/interactionLedger';
import logger from '../logger';

const UrlSync = () => {
  const globalServices = useContext(GlobalStateContext);
  
  // Context should always be available since GlobalStateProvider wraps Routes
  // But guard against SSR or edge cases
  if (!globalServices?.appState) {
    return null;
  }
  
  // Hooks must be called unconditionally - service should always exist here
  const [appState, sendAppState] = useActor(globalServices.appState);
  const serviceRef = useRef(null);
  const timeoutRef = useRef(null);
  const [showLoadingToast, setShowLoadingToast] = useState(false);
  const urlSyncedRef = useRef(false);

  useEffect(() => {
    if (!globalServices.appState) {
      logger.warn('App state service not available for URL sync');
      return;
    }

    let watchPrimedSubscription = null;

    // Watch for when applicationPrimed becomes true
    // This ensures we catch the moment priming completes and sync URL
    watchPrimedSubscription = globalServices.appState.subscribe((state) => {
      if (state.context && state.context.applicationPrimed && serviceRef.current && !urlSyncedRef.current) {
        // App just became primed - sync URL once from XState values (no delay)
        try {
          const currentState = globalServices.appState.getSnapshot().context;
          logger.debug({
            selectedNoteId: currentState.selectedNoteId,
            selectedAreaOfFocusId: currentState.selectedAreaOfFocusId,
            selectedNotebookId: currentState.selectedNotebookId
          }, 'App primed, syncing URL from XState');
          serviceRef.current.syncUrl();

          // Hide toast after URL sync
          urlSyncedRef.current = true;
          setTimeout(() => {
            setShowLoadingToast(false);
          }, 300);
        } catch (error) {
          logger.error({ error }, 'Error syncing URL after priming');
          setShowLoadingToast(false);
        }
      }
    });

    // Wait for app to be fully primed before starting URL sync
    // This ensures we have access to notes/notebooks/AOFs for validation
    const checkAndStart = () => {
      const currentState = globalServices.appState.getSnapshot();
      const isPrimed = currentState.context.applicationPrimed;
      
      if (isPrimed && !serviceRef.current) {
        // Create URL sync service without Next.js router
        // We use window.history directly to avoid Next.js navigation events
        const service = createUrlSyncService({
          sendAppState,
          getAppState: () => {
            // Always get the latest state from the actor
            // This ensures URL is always built from XState values, never from URL
            const currentState = globalServices.appState.getSnapshot();
            return currentState.context;
          },
        });

        serviceRef.current = service;
        service.start();
        logger.info('URL sync service started after app was primed');

        // Show loading toast to prevent URL flashing
        const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
        const currentState = globalServices.appState.getSnapshot().context;
        const hasStateToSync = currentState.selectedNoteId || currentState.selectedAreaOfFocusId || currentState.selectedNotebookId;
        
        // Only show toast if we're on root and have state to sync (will update URL)
        if (currentPath === '/' && hasStateToSync && !urlSyncedRef.current) {
          setShowLoadingToast(true);
        }

        // Sync URL once after service starts - no multiple retries to prevent flashing
        // Use requestAnimationFrame to ensure it happens after render
        requestAnimationFrame(() => {
          if (serviceRef.current) {
            try {
              const currentState = globalServices.appState.getSnapshot().context;
              logger.debug({ 
                selectedNoteId: currentState.selectedNoteId,
                selectedAreaOfFocusId: currentState.selectedAreaOfFocusId,
                selectedNotebookId: currentState.selectedNotebookId
              }, 'Syncing URL with initial primed state from XState');
              serviceRef.current.syncUrl();
              
              // Hide toast after URL sync completes
              urlSyncedRef.current = true;
              setTimeout(() => {
                setShowLoadingToast(false);
              }, 300);
            } catch (error) {
              logger.error({ error }, 'Error syncing initial URL from state');
              setShowLoadingToast(false);
            }
          }
        });

        // Subscribe ONLY to URL-relevant state values with minimal processing
        // Track only selectedNoteId, selectedAreaOfFocusId, selectedNotebookId, and panels
        // Ignore all other state changes (selectedNote object, notes dictionary, etc.)
        let lastSyncedNoteId = null;
        let lastSyncedAofId = null;
        let lastSyncedNotebookId = null;
        let lastSyncedPanels = null;
        
        const subscription = globalServices.appState.subscribe((state) => {
          if (!state.context || !state.context.applicationPrimed || !serviceRef.current) {
            return;
          }
          
          const currentState = state.context;
          
          // Extract ONLY URL-relevant values - minimal extraction
          const { getActivePanel } = require('../utils/getActivePanel');
          const currentPanels = getActivePanel(currentState);
          const currentPanelsStr = JSON.stringify((currentPanels || []).sort());
          
          const currentNoteId = currentState.selectedNoteId || null;
          const currentAofId = currentState.selectedAreaOfFocusId || null;
          const currentNotebookId = currentState.selectedNotebookId || null;
          
          // Only proceed if at least one URL-relevant value changed
          const noteChanged = currentNoteId !== lastSyncedNoteId;
          const aofChanged = currentAofId !== lastSyncedAofId;
          const notebookChanged = currentNotebookId !== lastSyncedNotebookId;
          const panelsChanged = currentPanelsStr !== lastSyncedPanels;
          
          if (!noteChanged && !aofChanged && !notebookChanged && !panelsChanged) {
            return; // No URL-relevant changes, skip entirely
          }
          
          logger.info(
            {
              noteChanged,
              aofChanged,
              notebookChanged,
              panelsChanged,
              currentNoteId,
              lastSyncedNoteId,
              currentAofId,
              lastSyncedAofId,
            },
            'URL sync: Detected state change, will update URL'
          );
          
          // Track URL sync trigger in ledger
          interactionLedger.log(
            'urlSyncTrigger',
            'urlSync',
            {
              noteChanged,
              aofChanged,
              notebookChanged,
              panelsChanged,
              currentNoteId,
              previousNoteId: lastSyncedNoteId,
              currentAofId,
              previousAofId: lastSyncedAofId,
            },
            `URL sync triggered: note=${noteChanged ? 'changed' : 'same'}, aof=${aofChanged ? 'changed' : 'same'}, notebook=${notebookChanged ? 'changed' : 'same'}`
          );
          
          // Update tracking immediately to prevent duplicate processing
          lastSyncedNoteId = currentNoteId;
          lastSyncedAofId = currentAofId;
          lastSyncedNotebookId = currentNotebookId;
          lastSyncedPanels = currentPanelsStr;
          
          // Cancel any pending sync
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          
          // Sync URL immediately - no delay to prevent flashing
          // This happens synchronously to ensure URL updates before next render
          if (serviceRef.current) {
            try {
              serviceRef.current.syncUrl();
            } catch (error) {
              logger.error({ error }, 'Error syncing URL');
            }
          }
        });

        // Store subscription for cleanup
        serviceRef.current._subscription = subscription;
      } else if (!isPrimed) {
        // Check again in 500ms if not primed yet
        setTimeout(checkAndStart, 500);
      }
    };

    // Start checking immediately
    checkAndStart();

    // Cleanup function
    return () => {
      // Copy ref value to local variable for cleanup
      const timeoutId = timeoutRef.current;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (watchPrimedSubscription) {
        watchPrimedSubscription.unsubscribe();
      }
      if (serviceRef.current) {
        // Unsubscribe if subscription exists
        if (serviceRef.current._subscription) {
          serviceRef.current._subscription.unsubscribe();
        }
        serviceRef.current.stop();
        serviceRef.current = null;
      }
    };
  }, [globalServices.appState, sendAppState, appState.context.applicationPrimed]);

  // Render loading toast
  return <LoadingToast show={showLoadingToast} message="Loading..." />;
};

export default UrlSync;

